import { useCallback, useEffect, useRef } from "react";
import { Platform } from "react-native";

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

interface UseFocusTrapOptions {
  /** Whether the focus trap is active */
  isActive: boolean;
  /** Callback when Escape key is pressed */
  onEscape?: () => void;
  /** Whether to auto-focus the first element when trap activates */
  autoFocus?: boolean;
  /** Delay before auto-focusing (ms) - allows for render completion */
  autoFocusDelay?: number;
}

interface UseFocusTrapReturn {
  /** Ref to attach to the container element */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** Keyboard event handler to attach to the container */
  handleKeyDown: (e: React.KeyboardEvent) => void;
}

/**
 * Hook to trap keyboard focus within a container element.
 * Implements Tab/Shift+Tab cycling and optional Escape key handling.
 *
 * @example
 * const { containerRef, handleKeyDown } = useFocusTrap({
 *   isActive: isModalOpen,
 *   onEscape: closeModal,
 *   autoFocus: true,
 * });
 *
 * <div ref={containerRef} onKeyDown={handleKeyDown}>
 *   <button>First</button>
 *   <button>Last</button>
 * </div>
 */
export function useFocusTrap({
  isActive,
  onEscape,
  autoFocus = true,
  autoFocusDelay = 50,
}: UseFocusTrapOptions): UseFocusTrapReturn {
  const containerRef = useRef<HTMLDivElement>(null);
  const previousActiveElementRef = useRef<HTMLElement | null>(null);
  const hasAutoFocusedRef = useRef<boolean>(false);

  // Reset auto-focus flag when trap deactivates
  useEffect(() => {
    if (!isActive) {
      hasAutoFocusedRef.current = false;
    }
  }, [isActive]);

  // Store the previously focused element when trap activates
  useEffect(() => {
    if (Platform.OS !== "web" || typeof document === "undefined") {
      return;
    }

    if (isActive) {
      previousActiveElementRef.current = document.activeElement as HTMLElement;
    }
  }, [isActive]);

  // Get focusable elements within the container (cached with useMemo)
  const getFocusableElements = useCallback((): HTMLElement[] => {
    if (!containerRef.current) return [];

    const elements = Array.from(
      containerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
    );

    return elements.filter((el) => {
      // Filter out hidden or disabled elements
      if (el.offsetParent === null) return false;
      if ((el as HTMLButtonElement).disabled) return false;
      if (el.getAttribute("aria-hidden") === "true") return false;
      return true;
    });
  }, []);

  // Auto-focus first element when trap activates (only once per activation)
  useEffect(() => {
    if (
      Platform.OS !== "web" ||
      typeof document === "undefined" ||
      !isActive ||
      !autoFocus ||
      hasAutoFocusedRef.current
    ) {
      return;
    }

    const timer = setTimeout(() => {
      // Skip if already auto-focused
      if (hasAutoFocusedRef.current) {
        return;
      }

      // Skip if focus is already inside the container
      if (
        containerRef.current &&
        containerRef.current.contains(document.activeElement)
      ) {
        hasAutoFocusedRef.current = true;
        return;
      }

      const focusable = getFocusableElements();
      const firstElement = focusable[0] ?? containerRef.current;

      if (firstElement) {
        (firstElement as HTMLElement).focus();
        hasAutoFocusedRef.current = true;
      }
    }, autoFocusDelay);

    return () => clearTimeout(timer);
  }, [isActive, autoFocus, autoFocusDelay, getFocusableElements]);

  // Handle keyboard events for focus trapping
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (Platform.OS !== "web" || !isActive || !containerRef.current) {
        return;
      }

      // Handle Escape key
      if (e.key === "Escape" && onEscape) {
        e.preventDefault();
        e.stopPropagation();
        onEscape();
        return;
      }

      // Handle Tab key for focus trapping
      if (e.key === "Tab") {
        const focusable = getFocusableElements();

        if (focusable.length === 0) {
          e.preventDefault();
          return;
        }

        const firstElement = focusable[0];
        const lastElement = focusable[focusable.length - 1];
        const activeElement = document.activeElement;

        if (e.shiftKey) {
          // Shift+Tab: going backwards
          if (activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          // Tab: going forwards
          if (activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    },
    [isActive, onEscape, getFocusableElements]
  );

  // Return focus to previous element when trap deactivates
  useEffect(() => {
    if (Platform.OS !== "web") {
      return;
    }

    return () => {
      if (previousActiveElementRef.current) {
        // Use setTimeout to ensure this runs after the modal is fully closed
        setTimeout(() => {
          previousActiveElementRef.current?.focus();
        }, 0);
      }
    };
  }, [isActive]);

  return {
    containerRef,
    handleKeyDown,
  };
}

export default useFocusTrap;
